/**
 * AnimatedModal - A visually polished modal component with animations
 *
 * Features:
 * - Smooth backdrop fade with blur
 * - Content slides up with spring physics
 * - Optional header image with parallax effect
 * - Staggered content reveal
 * - Keyboard support (Escape to close)
 * - Focus trap for accessibility
 * - Multiple size options
 */

import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import audioManager from '../../services/audioManager';
import { useFocusTrap } from '../../hooks/useFocusTrap';

// Animation variants
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2, delay: 0.1 }
  }
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 30
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 30,
      delay: 0.05
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.15, ease: 'easeIn' }
  }
};

const contentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.15
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  }
};

// Size configurations
const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]'
};

const AnimatedModal = ({
  isOpen,
  onClose,
  title,
  subtitle = null,
  children,
  size = 'lg',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  // Header image props
  headerImage = null,
  headerImageAlt = '',
  headerHeight = 'h-48',
  // Footer props
  footer = null,
  // Styling props
  className = '',
  contentClassName = '',
  headerClassName = '',
}) => {
  // Handle close with sound
  const handleClose = useCallback(() => {
    audioManager.play('close');
    onClose();
  }, [onClose]);

  // Focus trap with escape key handling
  const { containerRef: modalRef } = useFocusTrap({
    isActive: isOpen,
    onEscape: closeOnEscape ? handleClose : undefined,
  });

  // Sound effects and body scroll management
  useEffect(() => {
    if (isOpen) {
      // Play open sound
      audioManager.play('open');
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      handleClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6 md:p-8"
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            variants={backdropVariants}
            onClick={handleBackdropClick}
          />

          {/* Modal Container */}
          <motion.div
            ref={modalRef}
            className={`
              relative w-full ${SIZES[size]} max-h-[90vh]
              bg-gradient-to-b from-bg-panel to-bg-darker
              border border-border-light rounded-lg
              shadow-[0_20px_60px_rgba(0,0,0,0.6)]
              flex flex-col overflow-hidden
              focus:outline-none
              ${className}
            `}
            variants={modalVariants}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'modal-title' : undefined}
          >
            {/* Header Image (optional) */}
            {headerImage && (
              <motion.div
                className={`relative ${headerHeight} overflow-hidden flex-shrink-0`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <motion.img
                  src={headerImage}
                  alt={headerImageAlt || title}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                />
                {/* Gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-bg-darker via-transparent to-transparent" />

                {/* Close button on image */}
                {showCloseButton && (
                  <motion.button
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center
                             bg-black/50 hover:bg-black/70 rounded-full
                             text-white/70 hover:text-white
                             transition-colors backdrop-blur-sm"
                    onClick={handleClose}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Close modal"
                  >
                    <X size={18} aria-hidden="true" />
                  </motion.button>
                )}
              </motion.div>
            )}

            {/* Header (when no image) */}
            {!headerImage && (title || showCloseButton) && (
              <div className={`
                flex justify-between items-center px-6 py-4
                border-b border-border-dark bg-black/20
                ${headerClassName}
              `}>
                <div>
                  {title && (
                    <motion.h2
                      id="modal-title"
                      className="text-xl sm:text-2xl font-semibold text-white tracking-wide"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      {title}
                    </motion.h2>
                  )}
                  {subtitle && (
                    <motion.p
                      className="text-sm text-text-muted mt-0.5"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.15 }}
                    >
                      {subtitle}
                    </motion.p>
                  )}
                </div>
                {showCloseButton && (
                  <motion.button
                    className="w-8 h-8 flex items-center justify-center
                             text-gray-500 hover:text-white
                             hover:bg-white/10 rounded-full
                             transition-all duration-200"
                    onClick={handleClose}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    transition={{ delay: 0.2 }}
                    aria-label="Close modal"
                  >
                    <X size={20} aria-hidden="true" />
                  </motion.button>
                )}
              </div>
            )}

            {/* Title on image (if header image exists) */}
            {headerImage && title && (
              <div className="px-6 pt-4 -mt-12 relative z-10">
                <motion.h2
                  id="modal-title"
                  className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {title}
                </motion.h2>
                {subtitle && (
                  <motion.p
                    className="text-sm text-text-lighter mt-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35 }}
                  >
                    {subtitle}
                  </motion.p>
                )}
              </div>
            )}

            {/* Content */}
            <motion.div
              className={`
                flex-1 overflow-y-auto custom-scrollbar
                px-6 py-4
                ${contentClassName}
              `}
              variants={contentVariants}
              initial="hidden"
              animate="visible"
            >
              {children}
            </motion.div>

            {/* Footer (optional) */}
            {footer && (
              <motion.div
                className="flex-shrink-0 px-6 py-4 border-t border-border-dark bg-black/20"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                {footer}
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Export item variants for use in modal content
export const ModalItem = ({ children, className = '' }) => (
  <motion.div variants={itemVariants} className={className}>
    {children}
  </motion.div>
);

// Preset modal for confirmations
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'danger', // 'danger' | 'primary' | 'success'
  isLoading = false
}) => {
  const variantStyles = {
    danger: 'bg-accent-red hover:bg-accent-red-dark',
    primary: 'bg-accent-blue hover:bg-accent-blue-dark',
    success: 'bg-accent-green hover:bg-emerald-600'
  };

  const handleConfirm = () => {
    audioManager.playClick();
    onConfirm();
  };

  const handleCancel = () => {
    audioManager.play('close');
    onClose();
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <motion.button
            className="px-4 py-2 bg-bg-panel text-text-lighter border border-border-light
                     rounded hover:bg-bg-card transition-colors"
            onClick={handleCancel}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isLoading}
          >
            {cancelText}
          </motion.button>
          <motion.button
            className={`px-4 py-2 text-white rounded transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed
                       ${variantStyles[confirmVariant]}`}
            onClick={handleConfirm}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <motion.span
                  className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
                Loading...
              </span>
            ) : confirmText}
          </motion.button>
        </div>
      }
    >
      <p className="text-text-lighter">{message}</p>
    </AnimatedModal>
  );
};

export default AnimatedModal;
